/**
 * formeDuPlan — ce que le reçu d'un `apply_edit_plan` dit de la forme posée par
 * ses étapes `format_apply` (#1947, spec #1858).
 *
 * WHY : le reçu d'un plan ne prouvait une étape de forme que par l'accusé de
 * l'éditeur (`effect:"changed"`). Or cet accusé a déjà dit « réussi » sur une
 * forme absente de l'écran : c'est la raison d'être de la relecture des verbes
 * de forme (`verificationDeForme.ts`). Constat dans la vraie application
 * (2026-10-05, acte de 180 paragraphes) : l'agent pose le gras de 37 montants
 * par deux plans, et leurs reçus rendent `ok` sans rien dire de la forme.
 *
 * Module pur : il ne connaît ni l'éditeur ni le navigateur. La relecture lui
 * est passée (`relireLActeApresLEcriture`, `ecritureDeForme.ts`) ; le jugement
 * de chaque étape et le reçu de chaque réglage sont ceux des verbes de forme
 * (`jugerLEtendue`, `recuDuReglage`), appelés, pas recopiés — les trois issues
 * (conforme, différent avec la valeur lue, non lisible avec la raison) et
 * leurs bornes en viennent.
 *
 * ## Ce que la relecture ne juge pas, et qu'elle dit « non lisible »
 *
 * Une étape est relue comme UN passage de son paragraphe : le texte de l'étape,
 * trouvé une fois exactement dans le texte que le FICHIER donne du paragraphe.
 * Elle sort donc non lisible, jamais devinée, avec la raison qui est vraie :
 *
 * - `table_cell_not_reread` : le bloc visé est un TABLEAU, et l'aperçu de
 *   l'éditeur n'a pas dit dans quel paragraphe de cellule, un et un seul, il
 *   résout le passage : rien ne dit où relire. Le passage n'est pas
 *   « introuvable », il n'est pas relu. Non observé sur l'éditeur réel (relevé
 *   du 2026-10-07 : chaque étape de texte aperçue porte sa résolution) ;
 * - `target_not_found_on_reread` : le texte du paragraphe n'est pas le même
 *   pour l'éditeur et pour le fichier (appel de note, tiret insécable — en-tête
 *   de `formatTextExecutor.ts`) et le passage en porte la trace ; ou une autre
 *   étape du même plan a réécrit le paragraphe de sorte que le passage n'y
 *   figure plus une fois exactement.
 *
 * Un passage en cellule de tableau est relu comme un autre (§16 de
 * `superdoc-document-api.md`) : dans le paragraphe de cellule que l'étape
 * nomme, ou, quand elle nomme le tableau, dans celui où l'aperçu de l'éditeur
 * a résolu le passage. La relecture passée est celle du corps AVEC ses
 * cellules (`corpsAvecSesCellules`, `lecteurDeForme.ts`).
 *
 * Le statut suit la règle des verbes de forme pour un réglage « non lisible »
 * (`recuDeLEcritureDeForme`, erreur `FORMAT_NOT_VERIFIED`) : écrit, ni confirmé
 * ni infirmé, donc `partial`, sans rejeu.
 *
 * Sous suivi activé, une étape de forme laisse une révision de forme : le
 * lecteur rend la forme PROPOSÉE (celle que l'avocat voit), c'est elle qui est
 * jugée. Constaté sur l'éditeur réel pour le gras seulement
 * (`tests/ws-mocked/canvas-agent-style-live.spec.ts`, plan hétérogène suivi,
 * 2026-10-05 : reçu `ok`, donc l'étape de gras relue conforme) ; la garde de
 * ce module, `canvas-plan-forme-relue.spec.ts`, joue le plan suivi coupé.
 */

import {
  CANVAS_FORMAT_DIFFERENCE_TEXT_MAX,
  CANVAS_TEXT_FORMAT_SETTING_KEYS,
  type CanvasActionInput,
  type CanvasFormatSettingReceipt,
  type CanvasFormatSettingValue,
  type CanvasFormatUnreadableReason,
  type CanvasTextFormatSettingKey,
} from '@hector/shared-types';
import type { CanvasActionReceipt } from './actionReceipt';
import type { ResolutionDEtape } from './apercuDePlan';
import { normaliserLaDemande } from './reglagesDeTexte';
import { jugerLEtendue, recuDuReglage, type JugementDEtendue, type RelectureDeLActe } from './verificationDeForme';

type EtapeDePlan = Extract<CanvasActionInput, { action: 'apply_edit_plan' }>['steps'][number];
type Marque = Extract<EtapeDePlan, { op: 'format_apply' }>['mark'];

/** Nombre maximal d'étapes nommées dans un message : assez pour que l'agent les redise, sans recopier un plan de 50 étapes. Valeur de jugement. */
const ETAPES_NOMMEES_MAX = 10;

/** Une étape `format_apply` telle que la relecture la juge : son passage dans son paragraphe, et le réglage demandé. */
export interface EtapeDeFormeDuPlan {
  id: string;
  /**
   * Le paragraphe où le passage est relu : le bloc que le sélecteur de l'étape a désigné, ou, quand ce bloc est un
   * tableau, le paragraphe de cellule où l'aperçu de l'éditeur a résolu le passage.
   */
  nodeId: string;
  /** Le texte que l'étape met en forme : trouvé une fois dans ce bloc par l'éditeur. */
  texte: string;
  reglage: CanvasTextFormatSettingKey;
  /** La valeur demandée, sous la forme que rend le lecteur de forme (`normaliserLaDemande`). */
  demande: CanvasFormatSettingValue;
  /** Vrai si le bloc visé est un tableau dont l'aperçu n'a pas dit le paragraphe de cellule écrit : `nodeId` est alors le tableau, et rien n'est relu. */
  tableauSansParagraphe?: true;
}

/** Ce que la relecture dit de la forme posée par un plan. */
export interface FormeDuPlanRelue {
  /** Un reçu par réglage et par valeur demandée, dans l'ordre du contrat puis du plan. */
  settings: CanvasFormatSettingReceipt[];
  /** Les étapes dont le passage porte une autre valeur, avec la valeur lue. */
  differentes: Array<EtapeDeFormeDuPlan & { lu: CanvasFormatSettingValue }>;
  /** Les étapes dont le passage n'a pas pu être relu, avec la raison. */
  nonRelues: Array<EtapeDeFormeDuPlan & { raison: CanvasFormatUnreadableReason }>;
}

/** Le réglage et la valeur d'une marque : une marque de plan en porte exactement un. */
function reglageDeLaMarque(marque: Marque): { reglage: CanvasTextFormatSettingKey; valeur: CanvasFormatSettingValue } {
  if ('bold' in marque) return { reglage: 'bold', valeur: marque.bold };
  if ('italic' in marque) return { reglage: 'italic', valeur: marque.italic };
  if ('underline' in marque) return { reglage: 'underline', valeur: marque.underline };
  if ('highlight' in marque) return { reglage: 'highlight', valeur: marque.highlight };
  return { reglage: 'color', valeur: marque.color };
}

/**
 * Les étapes de forme d'un plan, dans l'ordre du plan.
 *
 * @param cibles Le bloc résolu de CHAQUE étape du plan, au même rang que `steps`, avec son type de nœud.
 * @param resolutions Ce que l'aperçu VALIDE du plan a résolu, par identifiant d'étape (`resolutionsParEtape`) : pour une
 *   étape qui vise un tableau, le paragraphe de cellule où l'éditeur écrit.
 */
export function etapesDeFormeDuPlan(
  steps: readonly EtapeDePlan[],
  cibles: ReadonlyArray<{ nodeId: string; nodeType: string }>,
  resolutions: ReadonlyMap<string, readonly ResolutionDEtape[]>,
): EtapeDeFormeDuPlan[] {
  return steps.flatMap((step, rang): EtapeDeFormeDuPlan[] => {
    const cible = cibles[rang];
    if (step.op !== 'format_apply' || cible === undefined) return [];
    const { reglage, valeur } = reglageDeLaMarque(step.mark);
    const etape = { id: step.id, texte: step.text, reglage, demande: normaliserLaDemande(reglage, valeur) };
    if (cible.nodeType !== 'table') return [{ ...etape, nodeId: cible.nodeId }];
    const [resolue, ...autres] = resolutions.get(step.id) ?? [];
    return [resolue === undefined || autres.length > 0 ? { ...etape, nodeId: cible.nodeId, tableauSansParagraphe: true } : { ...etape, nodeId: resolue.blockId }];
  });
}

/**
 * Le jugement d'une étape. Un tableau visé dont on ne sait pas quel paragraphe
 * de cellule a été écrit n'est pas confronté au fichier : aucun paragraphe n'y
 * porte l'identifiant du tableau, la vérification le dirait « introuvable », ce
 * qui est faux — il est écrit, et non relu.
 */
function jugerLEtape(relecture: RelectureDeLActe, etape: EtapeDeFormeDuPlan): JugementDEtendue {
  if (etape.tableauSansParagraphe === true) return { ecarts: [], nonLus: 1, raison: 'table_cell_not_reread', restes: [] };
  return jugerLEtendue(relecture, { nodeId: etape.nodeId, passage: etape.texte }, etape.reglage, etape.demande);
}

/**
 * Juge, sur le fichier relu après le plan, la forme que ses étapes ont posée.
 * Chaque étape est jugée UNE fois ({@link jugerLEtape}) : son jugement sert
 * le reçu du réglage et dit si l'étape diffère ou n'a pas pu être relue.
 *
 * @param relecture La forme relue dans le fichier exporté APRÈS le plan, ou la
 *   raison pour laquelle ce fichier ne le juge pas : aucune étape n'est alors
 *   dite conforme ni différente.
 * @param etapes Les étapes de forme du plan : au moins une. Un plan qui n'en
 *   porte pas ne se relit pas — c'est l'exécuteur qui le décide, avant d'exporter.
 */
export function jugerLaFormeDuPlan(relecture: RelectureDeLActe, etapes: readonly EtapeDeFormeDuPlan[]): FormeDuPlanRelue {
  const jugements = new Map(etapes.map((etape) => [etape, jugerLEtape(relecture, etape)]));
  const settings = CANVAS_TEXT_FORMAT_SETTING_KEYS.flatMap((reglage) => {
    const duReglage = etapes.filter((etape) => etape.reglage === reglage);
    return [...new Set(duReglage.map((etape) => etape.demande))].map((demande) => (
      recuDuReglage(reglage, demande, duReglage.filter((etape) => etape.demande === demande).flatMap((etape) => jugements.get(etape) ?? []))
    ));
  });
  const relue: FormeDuPlanRelue = { settings, differentes: [], nonRelues: [] };
  for (const [etape, { ecarts: [ecart], raison }] of jugements) {
    if (ecart !== undefined) relue.differentes.push({ ...etape, lu: ecart.read });
    else if (raison !== undefined) relue.nonRelues.push({ ...etape, raison });
  }
  return relue;
}

/** Des étapes dites une à une jusqu'à la borne, comptées au-delà. */
function direLesEtapes<Etape>(etapes: readonly Etape[], dire: (etape: Etape) => string): string {
  const nonNommees = etapes.length - ETAPES_NOMMEES_MAX;
  return `${etapes.slice(0, ETAPES_NOMMEES_MAX).map(dire).join('; ')}${nonNommees > 0 ? `; and ${nonNommees} more (editPlan.format.settings counts them)` : ''}`;
}

/** Ce qu'une raison de non-relecture veut dire, quand son seul nom ne dit pas à l'agent quoi en conclure. */
const RAISON_EN_CLAIR: Partial<Record<CanvasFormatUnreadableReason, string>> = {
  table_cell_not_reread: 'the block is a table and the editor did not report which cell paragraph it wrote',
};

/** La raison d'une étape non relue, telle que le message la dit. */
const direLaRaison = (raison: CanvasFormatUnreadableReason): string => {
  const enClair = RAISON_EN_CLAIR[raison];
  return enClair === undefined ? raison : `${raison} — ${enClair}`;
};

/**
 * Où lire ce que le document porte, après une forme écrite et non relue. Un
 * bloc qui est un paragraphe se lit par son identifiant ; un TABLEAU n'en est
 * pas un — `formatOf` le rendrait `not_found` — et rien ne dit laquelle de ses
 * cellules a été écrite : la lecture de tout l'acte les rend, à leur place.
 */
function recoursApresUneFormeNonRelue(nonRelues: ReadonlyArray<{ raison: CanvasFormatUnreadableReason }>): string {
  const surUnTableau = nonRelues.filter((etape) => etape.raison === 'table_cell_not_reread').length;
  return [
    ...(surUnTableau === nonRelues.length ? [] : ['A form read (canvas_inspect with formatOf) of their paragraphs shows what the document carries now.']),
    ...(surUnTableau === 0 ? [] : ['For a step on a table (table_cell_not_reread), read the form of the whole act (canvas_inspect with formatOf {kind:"all"}): it lists the cell paragraphs of the table, in document order.']),
  ].join(' ');
}

/** Le passage d'une étape, borné comme les exemples d'un réglage « différent ». */
const passageDe = (etape: EtapeDeFormeDuPlan): string => JSON.stringify(etape.texte.slice(0, CANVAS_FORMAT_DIFFERENCE_TEXT_MAX));

/**
 * Ajoute au reçu d'un plan APPLIQUÉ ce que la relecture dit de sa forme :
 * la section `editPlan.format`, le contrôle de relecture, et — pour une étape
 * dont la forme diffère ou n'a pas pu être relue — une erreur qui nomme
 * l'étape et son passage. Un reçu `ok` devient alors `partial`, jamais à
 * rejouer : le plan est écrit. Tout autre statut est gardé, avec ses erreurs.
 *
 * @param forme `undefined` : le plan ne porte aucune étape de forme, le reçu est rendu tel quel.
 */
export function recuAvecLaFormeDuPlan(
  recu: CanvasActionReceipt & { editPlan: NonNullable<CanvasActionReceipt['editPlan']> },
  forme: FormeDuPlanRelue | undefined,
): CanvasActionReceipt {
  if (forme === undefined) return recu;
  const { differentes, nonRelues } = forme;
  const erreurs: CanvasActionReceipt['errors'] = [
    ...(differentes[0] === undefined ? [] : [{
      code: 'EDIT_PLAN_FORMAT_NOT_AS_REQUESTED',
      stepId: differentes[0].id,
      message: `apply_edit_plan was applied, but the document re-read after the write does not show the formatting requested by ${differentes.length} step(s): `
        + `${direLesEtapes(differentes, (etape) => `"${etape.id}" (${etape.reglage} ${JSON.stringify(etape.demande)} on ${passageDe(etape)} in block ${etape.nodeId}: read ${JSON.stringify(etape.lu)})`)}. `
        + 'editPlan.format.settings gives, for each setting, the value actually read. Do not resend the plan: it is written. Tell the lawyer exactly which passages differ.',
    }]),
    ...(nonRelues[0] === undefined ? [] : [{
      code: 'EDIT_PLAN_FORMAT_NOT_VERIFIED',
      stepId: nonRelues[0].id,
      message: `apply_edit_plan was applied, but the formatting of ${nonRelues.length} step(s) could not be re-read from the document: `
        + `${direLesEtapes(nonRelues, (etape) => `"${etape.id}" (${passageDe(etape)} in block ${etape.nodeId}: ${direLaRaison(etape.raison)})`)}. `
        + 'It is neither confirmed nor refuted: do not resend the plan. Tell the lawyer these passages were formatted but could not be checked. '
        + recoursApresUneFormeNonRelue(nonRelues),
    }]),
  ];
  const conforme = erreurs.length === 0;
  return {
    ...recu,
    ...(conforme ? {} : {
      status: recu.status === 'ok' ? 'partial' as const : recu.status,
      verificationPassed: false,
      errors: [...recu.errors, ...erreurs],
      recovery: recu.recovery ?? { action: 'stop' as const, reason: 'The plan is written; the receipt names the formatting that is not as requested, and resending the plan would not change it.' },
    }),
    verification: [...recu.verification, {
      name: 'plan_format_reread_from_exported_file',
      passed: conforme,
      message: 'Every format_apply step must be read back, as requested, from the Word file exported after the plan.',
    }],
    editPlan: { ...recu.editPlan, format: { settings: forme.settings } },
  };
}
